const MIN_REDEMPTION_AMOUNT = 100

const REDEMPTION_MODES = {
  DYNAMIC: 'dynamic',
  CUSTOM: 'custom',
}

const getHoldingCurrentValue = (fund) => {
  const explicit = Number(fund?.currentAmount)
  if (Number.isFinite(explicit) && explicit > 0) return explicit

  const units = Number(fund?.netUnits ?? fund?.units ?? 0)
  const nav = Number(fund?.currentNav ?? fund?.nav ?? 0)
  if (units > 0 && nav > 0) return units * nav

  const fallback = Number(fund?.currentValue ?? fund?.holding ?? 0)
  return Number.isFinite(fallback) ? fallback : 0
}

const getFundIdentity = (fund, index = 0) =>
  [
    fund?.isin || 'isin',
    fund?.bseSchemeCode || fund?.schemeCode || 'scheme',
    fund?.folioNo || fund?.folioNumber || fund?.folio || 'folio',
    index,
  ].join('-')

const getSchemeCode = (fund) => fund?.bseSchemeCode || fund?.schemeCode || ''

const getSchemeName = (fund) => fund?.legalName || fund?.schemeName || 'Fund'

const roundRupee = (value) => {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return 0
  return Math.max(0, Math.round(amount))
}

const getDynamicFundAmount = (fund) => {
  if (fund?.redemptionType === 'VALUE') return Number(fund?.withdrawableAmount || 0)
  if (fund?.redemptionType === 'UNITS') {
    const units = Number(fund?.withdrawableUnits ?? fund?.qty ?? 0)
    return units * Number(fund?.nav || 0)
  }
  if (fund?.withdrawableAmount != null) return Number(fund.withdrawableAmount)
  return Number(fund?.currentValue || 0)
}

const toLine = (fund, amount, index, source) => {
  const currentValue = roundRupee(getHoldingCurrentValue(fund))
  const redeemAmount = roundRupee(amount)
  return {
    id: getFundIdentity(fund, index),
    schemeName: getSchemeName(fund),
    amcCode: fund?.amcCode || '',
    assetClass: fund?.assetClass || '',
    isin: fund?.isin || '',
    folioNo: fund?.folioNo || fund?.folioNumber || fund?.folio || '',
    schemeCode: getSchemeCode(fund),
    nav: Number(fund?.currentNav ?? fund?.nav ?? 0),
    currentValue: currentValue || redeemAmount,
    amount: redeemAmount,
    redemptionType: fund?.redemptionType || 'VALUE',
    withdrawableUnits: fund?.withdrawableUnits,
    source,
    sourceFund: fund,
  }
}

const buildDynamicLines = (funds) =>
  (funds || [])
    .map((fund, index) => toLine(fund, getDynamicFundAmount(fund), index, REDEMPTION_MODES.DYNAMIC))
    .filter((line) => line.amount > 0)

const sumLines = (lines) => (lines || []).reduce((sum, line) => sum + roundRupee(line.amount), 0)

const withWeightage = (lines) => {
  const total = sumLines(lines)
  return (lines || []).map((line) => ({
    ...line,
    weightage: total > 0 ? (roundRupee(line.amount) / total) * 100 : 0,
  }))
}

const toDynamicOrderBreakdown = (funds) =>
  (funds || [])
    .map((fund) => {
      const allocationAmount =
        fund?.allocationAmount != null ? fund.allocationAmount : getDynamicFundAmount(fund)
      return {
        ...fund,
        allocationAmount,
        investmentAmount: allocationAmount,
      }
    })
    .filter((fund) => Number(fund.allocationAmount) > 0)

const toOrderBreakdown = (lines) =>
  withWeightage(lines)
    .filter((line) => line.amount > 0)
    .map((line) => {
      const fullRedeem = line.redemptionType === 'ALL_UNITS'
      return {
        ...(line.sourceFund || {}),
        allocationAmount: line.amount,
        investmentAmount: line.amount,
        withdrawableAmount: fullRedeem ? undefined : line.amount,
        weightage: Number(line.weightage.toFixed(4)),
        redemptionType: fullRedeem ? 'ALL_UNITS' : line.redemptionType || 'VALUE',
        isAllRedeem: fullRedeem ? 'Y' : 'N',
      }
    })

const getLineError = (line) => {
  if (!line || line.amount <= 0) return ''
  if (line.redemptionType === 'ALL_UNITS') return ''
  if (line.currentValue > 0 && line.amount > line.currentValue) {
    return 'Amount is higher than this fund’s current value'
  }
  if (line.amount < MIN_REDEMPTION_AMOUNT) {
    return `Minimum redemption is ₹${MIN_REDEMPTION_AMOUNT}`
  }
  return ''
}

const getBreakdownError = (lines, availableAmount) => {
  const active = (lines || []).filter((line) => line.amount > 0)
  if (!active.length) return 'Keep at least one fund in this redemption'
  const lineError = active.map(getLineError).find(Boolean)
  if (lineError) return lineError
  const total = sumLines(active)
  if (total <= 0) return 'Enter a redemption amount'
  if (availableAmount > 0 && total > roundRupee(availableAmount)) {
    return 'Total is higher than the available balance'
  }
  return ''
}

const extractOrderList = (response) => {
  if (!response || response.isError) return []

  let payload = response
  if (typeof response?.data === 'string') {
    try {
      payload = JSON.parse(response.data)
    } catch {
      payload = response
    }
  }

  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.orders)) return payload.orders
  if (Array.isArray(response?.data)) return response.data
  return []
}

const isRedeemOrder = (order) => String(order?.orderType || '').toUpperCase() === 'REDEEM'

export {
  MIN_REDEMPTION_AMOUNT,
  REDEMPTION_MODES,
  getHoldingCurrentValue,
  buildDynamicLines,
  sumLines,
  toDynamicOrderBreakdown,
  toOrderBreakdown,
  getLineError,
  getBreakdownError,
  extractOrderList,
  isRedeemOrder,
  roundRupee,
}
